import { useEffect, useRef, useState, useCallback } from 'react';

// DOM events that count as "the user is present". Note the Clerk token refresh
// is a network call, NOT a DOM event, so it does not reset this — which is the
// whole point: an open but unattended tab must still time out.
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
];

// Shared across tabs via localStorage: the last real-activity timestamp, and a
// logout broadcast so signing out in one tab signs out the others.
const LS_LAST_ACTIVITY = 'atlas.idle.lastActivity';
const LS_LOGOUT = 'atlas.idle.logout';

// Don't write to localStorage on every mousemove.
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

/**
 * Idle auto-logout.
 *
 * Tracks real user activity (shared across tabs), shows a warning during the
 * final `warningMs`, and calls `onTimeout` once total idle exceeds `timeoutMs`.
 *
 * Uses wall-clock comparisons (now - lastActivity) checked on a 1s interval and
 * on focus/visibility change, so a backgrounded or asleep tab still logs out
 * promptly on return instead of relying on setTimeout (which browsers throttle
 * in the background).
 *
 * Returns { warningSecondsLeft, stayActive }: warningSecondsLeft is null when
 * not warning, otherwise the countdown; stayActive resets the timer.
 */
export function useIdleLogout({ enabled, timeoutMs, warningMs, onTimeout }) {
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const firedRef = useRef(false);
  const lastWriteRef = useRef(0);

  const markActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastWriteRef.current = now;
    try {
      localStorage.setItem(LS_LAST_ACTIVITY, String(now));
    } catch (_) {
      /* localStorage unavailable — fall back to in-memory only */
    }
  }, []);

  // "Stay signed in" / any deliberate reset: bypass the throttle and clear the
  // warning immediately.
  const stayActive = useCallback(() => {
    lastWriteRef.current = 0;
    markActivity();
    setWarningSecondsLeft(null);
  }, [markActivity]);

  useEffect(() => {
    if (!enabled) {
      setWarningSecondsLeft(null);
      return undefined;
    }
    firedRef.current = false;
    lastWriteRef.current = 0;
    markActivity(); // seed: start the idle clock now

    const getLastActivity = () => {
      const v = parseInt(localStorage.getItem(LS_LAST_ACTIVITY) || '', 10);
      return Number.isFinite(v) ? v : Date.now();
    };

    const doLogout = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      try {
        localStorage.setItem(LS_LOGOUT, String(Date.now()));
      } catch (_) {
        /* ignore */
      }
      if (onTimeoutRef.current) onTimeoutRef.current();
    };

    const check = () => {
      const idle = Date.now() - getLastActivity();
      if (idle >= timeoutMs) {
        setWarningSecondsLeft(null);
        doLogout();
      } else if (idle >= timeoutMs - warningMs) {
        setWarningSecondsLeft(Math.max(0, Math.ceil((timeoutMs - idle) / 1000)));
      } else {
        setWarningSecondsLeft(null);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onStorage = (ev) => {
      if (ev.key === LS_LOGOUT && ev.newValue) doLogout();
    };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, markActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    window.addEventListener('storage', onStorage);
    const interval = setInterval(check, 1000);
    check();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [enabled, timeoutMs, warningMs, markActivity]);

  return { warningSecondsLeft, stayActive };
}
